import React, { useEffect, useState, useContext } from 'react';
import {
  CircularProgress,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Badge,
  IconButton
} from '@mui/material';

import { Visibility, PictureAsPdf, ExpandMore } from '@mui/icons-material';
import PaymentIcon from '@mui/icons-material/Payment';
import { useParams, useNavigate } from 'react-router-dom';

import documentoApi from '../../api/documentoApi';
import scadenzaApi from '../../api/scadenzaApi';
import ProtectedAction from '../../components/ProtectedAction';
import { AuthContext } from '../../auth/AuthContext';
import { scadenzaStatusMap } from '../../utils/scadenzaStatusMapper';
import { formatDate } from '../../utils/dateFormatter';
import BackButtonDesktop from '../../components/BackButtonDesktop';
import QRCode from 'react-qr-code';
import { QrCode2 } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

export default function DocumentoDetailPageMobile() {

  const { id } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [scadenze, setScadenze] = useState([]);
  const [openQr, setOpenQr] = useState(false);
  const [qrValue, setQrValue] = useState(null);

  const truncateText = (text, max = 120) =>
    text && text.length > max ? text.slice(0, max) + '...' : text;

  useEffect(() => {
    if (id) {
      fetchDocumento();
      fetchScadenze();
    }
  }, [id]);

  const fetchDocumento = async () => {
    const r = await documentoApi.get(id);
    setDoc(r.data);
  };

  const fetchScadenze = async () => {
    if (!token) return;

    try {
      const r = await scadenzaApi.listbydocument(id, { page: 0, size: 1000 });
      setScadenze(r.data.content || []);
    } catch (err) {
      console.error(err);
      setScadenze([]);
    }
  };

  const handleDownload = async (documentoId, fileName) => {
    try {
      setLoading(true);

      const response = await documentoApi.downloadDocumento(documentoId, fileName);

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );

      window.open(url, '_blank');

    } catch (err) {
      console.error('Errore download PDF', err);
    } finally {
      setLoading(false);
    }
  };

  const openQrPopup = (codice) => {
    setQrValue(codice);
    setOpenQr(true);
  };

  if (!doc) {
    return (
      <Container sx={{ mt: 2 }}>
        <Typography>Caricamento documento…</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 2, pb: 4 }}>

      <BackButtonDesktop />

      {/* LOADING */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}

      {/* HEADER */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h5">
          {doc.tipo}
        </Typography>

        {doc.rateSaltate > 0 && (
          <Badge
            color="error"
            badgeContent={doc.rateSaltate}
            sx={{
              '& .MuiBadge-badge': {
                position: 'static',
                transform: 'none'
              }
            }}
          />
        )}
      </Stack>

      {/* DESCRIZIONE */}
      <Typography variant="subtitle1" sx={{ mt: 1 }}>
        {truncateText(doc.descrizione, 120)}
      </Typography>

      {/* INFO */}
      <Typography variant="body2">
        Contribuente: {doc.contribuente?.nome} {doc.contribuente?.cognome}
      </Typography>

      <Typography variant="body2">
        Data creazione: {formatDate(doc.dataCreazione)}
      </Typography>

      {/* PDF PRINCIPALE */}
      <Stack spacing={2} sx={{ mt: 2 }}>

        {doc.pdfPath && (
          <Card>
            <CardContent>
              <Typography fontWeight={600}>
                PDF Principale
              </Typography>

              <IconButton
                color="error"
                onClick={() => handleDownload(doc.id, doc.pdfPath)}
              >
                <PictureAsPdf />
              </IconButton>
            </CardContent>
          </Card>
        )}

        {/* ALLEGATI */}
        {doc.files?.map(f => (
          <Card key={f.id}>
            <CardContent>
              <Typography fontWeight={600}>
                Allegato
              </Typography>

              <Typography variant="caption">
                {formatDate(f.uploadDate)}
              </Typography>
              <Tooltip title="download file">
              <IconButton
                color="error"
                onClick={() => handleDownload(doc.id, f.filename)}
              >
                <PictureAsPdf />
              </IconButton>
              </Tooltip>
            </CardContent>
          </Card>
        ))}

        {/* SCADENZE */}
        <Typography variant="h6" sx={{ mt: 2 }}>
          Scadenze collegate
        </Typography>

        {scadenze.map(s => (
          <Accordion key={s.id} disableGutters>

            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <Typography variant="body2">
                  {s.tipo} - {formatDate(s.dataScadenza)} - €{s.importo}
                </Typography>

                <Chip
                  label={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.label || s.stato}
                  color={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.color || 'default'}
                  size="small"
                />
              </Stack>
            </AccordionSummary>

            <AccordionDetails>

              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="dettaglio">
                <IconButton
                  color="primary"
                  onClick={() => navigate(`/scadenze/${s.id}`)}
                >
                  <Visibility />
                </IconButton>
                </Tooltip>

                {s.pdfPath && (
                <Tooltip title="download file">
                  <IconButton
                    color="error"
                    onClick={() => handleDownload(doc.id, s.pdfPath)}
                  >
                    <PictureAsPdf />
                  </IconButton>
                  </Tooltip>
                )}
                {s.qrcode && s.qrcode!=="" && s.stato !== "pagata" && (
                  <Tooltip title="paga">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => openQrPopup(s.qrcode)}
                    >
                      <QrCode2 />
                    </IconButton>
                  </Tooltip>
                )}
                {s.stato !== 'pagata' && (
                  <ProtectedAction user={user} contractState={user.statoContratto}>
                    <Tooltip title="marca come pagata">
                    <IconButton
                      color="success"
                      onClick={async () => {
                        await scadenzaApi.setpagatamanuale(s.id);
                        fetchScadenze();
                      }}
                    >
                      <PaymentIcon />
                    </IconButton>
                    </Tooltip>
                  </ProtectedAction>
                )}

              </Stack>

            </AccordionDetails>

          </Accordion>
        ))}

      </Stack>
        <Dialog open={openQr} onClose={() => setOpenQr(false)}>
          <DialogTitle>Leggi il qrcode CBILL/PagoPa con l'app della tua banca</DialogTitle>
          <DialogContent>
            {qrValue && (
              <Box textAlign="center" p={2}>
                <QRCode value={qrValue} />
                <Typography mt={2}>{qrValue}</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
    </Container>
  );
}
